/**
 * guestGateStore
 *
 * Centralised gate for account-required actions in guest preview sessions.
 * A single GuestClaimModal is mounted once at the app root and driven by this
 * store, so any handler anywhere can gate itself with a one-liner:
 *
 *   import { requireAccount } from '../stores/guestGateStore';
 *   if (!requireAccount('create a new space')) return;
 *
 * requireAccount() returns true for real (non-guest) users so the action
 * proceeds normally; for guests it opens the claim modal and returns false.
 */

import { create } from 'zustand';
import { isGuestSession } from '../hooks/useGuestMode';

interface GuestGateState {
  isOpen: boolean;
  actionLabel?: string;
  open: (actionLabel?: string) => void;
  close: () => void;
}

export const useGuestGateStore = create<GuestGateState>((set, get) => ({
  isOpen: false,
  actionLabel: undefined,
  // No-op while already open with the same label — guards against duplicate/
  // rapid re-entrant calls (e.g. a double-click, or two gated handlers firing
  // in the same tick) re-triggering the modal's mount/open transition.
  open: (actionLabel) => {
    const state = get();
    if (state.isOpen && state.actionLabel === actionLabel) return;
    set({ isOpen: true, actionLabel });
  },
  close: () => set({ isOpen: false }),
}));

/**
 * Gate an account-required action.
 * @returns true if the action is allowed (user is not a guest);
 *          false if the user is a guest (the claim modal is opened instead).
 */
export function requireAccount(actionLabel?: string): boolean {
  if (!isGuestSession()) return true;
  useGuestGateStore.getState().open(actionLabel);
  return false;
}
